import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Clock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

interface SessionExpiredModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SessionExpiredModal({
  isOpen,
  onClose,
}: SessionExpiredModalProps) {
  const navigate = useNavigate();
  const { sessionExpired } = useAuth();

  const getReturnUrl = React.useCallback(() => {
    // 1. Check if we already have a returnUrl in search params (e.g. if we got redirected to /login?returnUrl=...)
    const params = new URLSearchParams(window.location.search);
    const paramReturnUrl = params.get("returnUrl");
    if (paramReturnUrl) return paramReturnUrl;

    // 2. Check if we have one in storage
    const storedUrl = sessionStorage.getItem("post_session_expired_redirect") || localStorage.getItem("post_session_expired_redirect");
    if (storedUrl) return storedUrl;

    // 3. Otherwise use the current path if not login/signup
    const currentPath = window.location.pathname + window.location.search;
    if (!currentPath.startsWith("/login") && !currentPath.startsWith("/signup")) {
      return currentPath;
    }
    return "";
  }, []);

  const redirectToLogin = React.useCallback(() => {
    const currentHost = window.location.hostname;
    const isLocalhost = currentHost === "localhost" || currentHost === "127.0.0.1" || currentHost.startsWith("192.168.");
    const returnUrl = getReturnUrl();
    const encodedReturnUrl = returnUrl ? encodeURIComponent(returnUrl) : "";
    const loginPath = encodedReturnUrl ? `/login?returnUrl=${encodedReturnUrl}` : "/login";

    if (isLocalhost || currentHost.startsWith("app.")) {
      navigate(loginPath);
    } else {
      const protocol = window.location.protocol;
      const port = window.location.port ? `:${window.location.port}` : "";
      
      let loginHost = currentHost;
      if (!currentHost.startsWith("app.")) {
        if (currentHost.startsWith("www.")) {
          loginHost = currentHost.replace("www.", "app.");
        } else {
          loginHost = `app.${currentHost}`;
        }
      }
      window.location.href = `${protocol}//${loginHost}${port}${loginPath}`;
    }
  }, [navigate, getReturnUrl]);

  useEffect(() => {
    if (isOpen) {
      // Show appropriate toast notification based on expiration type
      const message = sessionExpired
        ? "Sesión expirada por inactividad"
        : "Sesión expirada";

      const description = sessionExpired
        ? "Tu sesión ha expirado por inactividad. Serás redirigido a iniciar sesión."
        : "Tu sesión ha expirado. Serás redirigido a iniciar sesión.";

      toast.error(message, {
        description,
        duration: 5000,
      });

      // Auto-redirect to login page after 5 seconds
      const timer = setTimeout(() => {
        redirectToLogin();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, sessionExpired, redirectToLogin]);

  const handleGoToLogin = () => {
    onClose();
    redirectToLogin();
  };

  const getModalContent = () => {
    if (sessionExpired) {
      return {
        title: "Sesión Expirada por Inactividad",
        description:
          "Tu sesión ha expirado por inactividad. Por seguridad, has sido desconectado automáticamente.",
        icon: <Clock className="h-5 w-5 text-orange-500" />,
        details:
          "Serás redirigido a la página de inicio de sesión en unos segundos, o puedes hacer clic en el botón de abajo para ingresar nuevamente.",
      };
    }

    return {
      title: "Sesión Expirada",
      description:
        "Tu sesión ha expirado. Por seguridad, has sido desconectado automáticamente.",
      icon: <AlertTriangle className="h-5 w-5 text-orange-500" />,
      details:
        "Serás redirigido a la página de inicio de sesión en unos segundos, o puedes hacer clic en el botón de abajo para ingresar nuevamente.",
    };
  };

  const content = getModalContent();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {content.icon}
            <DialogTitle>{content.title}</DialogTitle>
          </div>
          <DialogDescription className="text-left">
            {content.description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{content.details}</p>
          <Button onClick={handleGoToLogin} className="w-full">
            <LogIn className="mr-2 h-4 w-4" />
            Iniciar Sesión
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
